// The device heartbeat (04 §4.6, 10 Phase 3 gap "nothing writes devices/{id}"): the phone tells the
// server it is alive, how many commands are still waiting on it and when it last synced, so day
// close can see an unsynced phone and support can see a lost one. A driver on an active run also
// sends where the van is, at most every two minutes, and only when the person has already allowed
// location (the heartbeat never asks). It never throws: a failed report waits for the next one.
import type { Functions } from 'firebase/functions';
import type { Connectivity } from './connectivity';
import { callReportDevice, type ReportDeviceInput } from './finalmile';
import type { StopGeo } from './ops-finalmile';
import { positionToGeo } from './ops-finalmile';
import type { ReadableStore } from './store';
import type { SyncSnapshot } from './sync';

/** What the `reportDevice` callable takes: one contract for the office and the phone (04 §4.7). */
export type DeviceReport = ReportDeviceInput;

/** The one `reportDevice` wrapper, shared with the office (`callReportDevice`). */
export const reportDevice = (functions: Functions, input: DeviceReport): Promise<void> =>
  callReportDevice(functions, input);

/** Every few minutes while the app is open and online. */
export const HEARTBEAT_EVERY_MS = 3 * 60_000;
/** A geo ping at most this often. */
export const GEO_EVERY_MS = 2 * 60_000;
/** Reports caused by a sync never come closer than this. */
export const HEARTBEAT_MIN_GAP_MS = 20_000;

export type BeatReason = 'start' | 'online' | 'interval' | 'sync' | 'manual';

export interface HeartbeatDeps {
  deviceId: string;
  appVersion: string;
  connectivity: Pick<Connectivity, 'getSnapshot' | 'subscribe'>;
  sync: ReadableStore<Pick<SyncSnapshot, 'pending' | 'lastSyncedAt'>>;
  /** Sends the report (the `reportDevice` callable). */
  send: (report: DeviceReport) => Promise<unknown>;
  /** Only a signed-in, active person reports. */
  canReport: () => boolean;
  /** The branch the person is working at (sent with each report). */
  getBranchId?: () => string | null | undefined;
  /** True while this person has a run in progress: only then is a position sent. */
  onActiveRun?: () => boolean;
  /** A position, or undefined when location is not allowed or not available. */
  getGeo?: () => Promise<StopGeo | undefined>;
  now?: () => number;
  everyMs?: number;
  geoEveryMs?: number;
  minGapMs?: number;
  /** Set false in tests that call `beat` by hand. */
  autoTimer?: boolean;
}

export interface Heartbeat {
  /** Sends one report now. Returns true when it was sent. Never throws. */
  beat(reason?: BeatReason): Promise<boolean>;
  /** Starts the beats: app start, every few minutes, back online, after each sync. Returns a stop function. */
  start(): () => void;
}

export function createHeartbeat(deps: HeartbeatDeps): Heartbeat {
  const {
    deviceId,
    appVersion,
    connectivity,
    sync,
    send,
    canReport,
    getBranchId,
    onActiveRun,
    getGeo,
    now = Date.now,
    everyMs = HEARTBEAT_EVERY_MS,
    geoEveryMs = GEO_EVERY_MS,
    minGapMs = HEARTBEAT_MIN_GAP_MS,
    autoTimer = true,
  } = deps;
  const startedAt = now();
  let lastBeatAt = -Infinity;
  let lastGeoAt = -Infinity;
  let running: Promise<boolean> | undefined;

  const beat = (reason: BeatReason = 'manual'): Promise<boolean> => {
    if (running) return running;
    if (!connectivity.getSnapshot().online || !canReport()) return Promise.resolve(false);
    const t = now();
    // A sync can happen many times a minute; the first beat and a return to signal always go.
    if ((reason === 'sync' || reason === 'interval') && t - lastBeatAt < minGapMs) {
      return Promise.resolve(false);
    }
    running = (async () => {
      try {
        const state = sync.getSnapshot();
        let geo: StopGeo | undefined;
        if (getGeo && onActiveRun?.() === true && t - lastGeoAt >= geoEveryMs) {
          try {
            geo = await getGeo();
          } catch (err) {
            console.warn('The position could not be read for the heartbeat.', err);
          }
          if (geo) lastGeoAt = t;
        }
        const branchId = getBranchId?.();
        await send({
          deviceId,
          app: 'ops',
          appVersion,
          pendingCount: state.pending,
          lastSyncAt: new Date(state.lastSyncedAt ?? startedAt).toISOString(),
          ...(branchId ? { branchId } : {}),
          ...(geo ? { geo } : {}),
        });
        lastBeatAt = t;
        return true;
      } catch (err) {
        console.warn('The device heartbeat could not be sent. It will try again.', err);
        return false;
      } finally {
        running = undefined;
      }
    })();
    return running;
  };

  return {
    beat,
    start() {
      let wasOnline = connectivity.getSnapshot().online;
      let lastSync = sync.getSnapshot().lastSyncedAt;
      let lastPending = sync.getSnapshot().pending;
      void beat('start');
      const timer = autoTimer ? setInterval(() => void beat('interval'), everyMs) : undefined;
      const stopNet = connectivity.subscribe(() => {
        const online = connectivity.getSnapshot().online;
        if (online && !wasOnline) void beat('online');
        wasOnline = online;
      });
      const stopSync = sync.subscribe(() => {
        const s = sync.getSnapshot();
        const synced = s.lastSyncedAt !== lastSync;
        // "After each sync": the server has caught up (the clock moved) or my queue just emptied.
        const drained = s.pending === 0 && lastPending > 0;
        lastSync = s.lastSyncedAt;
        lastPending = s.pending;
        if (synced || drained) void beat('sync');
      });
      return () => {
        if (timer !== undefined) clearInterval(timer);
        stopNet();
        stopSync();
      };
    },
  };
}

/**
 * The phone's position, but only if the person already allowed location: the heartbeat must never
 * pop up a permission question in the middle of a delivery. Resolves undefined when not allowed,
 * not supported, or no fix arrives in time.
 */
export async function grantedPosition(
  nav: Pick<Navigator, 'geolocation' | 'permissions'> | undefined = typeof navigator === 'undefined'
    ? undefined
    : navigator,
  timeoutMs = 10_000,
): Promise<StopGeo | undefined> {
  if (!nav?.geolocation || !nav.permissions?.query) return undefined;
  try {
    const status = await nav.permissions.query({ name: 'geolocation' });
    if (status.state !== 'granted') return undefined;
  } catch (err) {
    console.warn('Location permission could not be checked.', err);
    return undefined;
  }
  const geolocation = nav.geolocation;
  return new Promise((resolve) => {
    geolocation.getCurrentPosition(
      (position) => resolve(positionToGeo(position)),
      () => resolve(undefined),
      { enableHighAccuracy: false, timeout: timeoutMs, maximumAge: 60_000 },
    );
  });
}
